import { sameAnswers, type PreCallAnswers } from "./pre-call-answers";

export interface PreCallAnswersSaver {
  /** Queues the latest answers; they are sent once taps stop for the
   * debounce delay. */
  schedule(answers: PreCallAnswers): void;
  /** Sends whatever is queued now and resolves once nothing is queued
   * or in flight. Never rejects. */
  flush(): Promise<void>;
  /** Drops the queued answers and the pending timer. */
  cancel(): void;
}

/**
 * Debounced, serialized writes of the family's latest answers: never two
 * saves in flight, the latest answers always win, and answers equal to
 * the last saved ones are not sent. A failed save is reported and the
 * queue left empty — the next change retries.
 */
export function createPreCallAnswersSaver(args: {
  saved: PreCallAnswers;
  delayMs: number;
  save: (answers: PreCallAnswers) => Promise<void>;
  onSaved: (answers: PreCallAnswers) => void;
  onFailed: (error: unknown) => void;
}): PreCallAnswersSaver {
  let lastSaved = args.saved;
  let queued: PreCallAnswers | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let draining: Promise<void> | null = null;

  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const run = async (): Promise<void> => {
    while (queued !== null) {
      const next: PreCallAnswers = queued;
      queued = null;
      if (sameAnswers(next, lastSaved)) continue;
      try {
        await args.save(next);
      } catch (error) {
        args.onFailed(error);
        return;
      }
      lastSaved = next;
      args.onSaved(next);
    }
  };

  const drain = (): Promise<void> => {
    if (draining) {
      return draining.then(() => (queued !== null ? drain() : undefined));
    }
    const current: Promise<void> = run().finally(() => {
      if (draining === current) draining = null;
    });
    draining = current;
    return current;
  };

  return {
    schedule(answers) {
      queued = answers;
      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        void drain();
      }, args.delayMs);
    },
    async flush() {
      clearTimer();
      await drain();
    },
    cancel() {
      clearTimer();
      queued = null;
    },
  };
}
